/**
 * @component NewsListPage
 * @description 공지/소식 콘텐츠를 페이징된 리스트로 제공합니다.
 *              관리자는 총 건수 텍스트를 5회 클릭해 관리자 모드에 진입할 수 있습니다.
 * @usage App 라우팅에서 `/news/index.html` 경로일 때 렌더링됩니다.
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import PageHero from '../../components/common/PageHero';
import PasswordVerifyModal from '../../components/contact/PasswordVerifyModal';
import { pageMeta } from '../../constants/navigation';

const NEWS_PAGE_SIZE = 10;
// [비즈니스] 일반 사용자가 우연히 관리자 모드를 발견하지 못하도록 5회 클릭 트리거를 사용합니다.
const ADMIN_TRIGGER_COUNT = 5;
const ADMIN_TRIGGER_RESET_MS = 3000;

const CATEGORIES = ['Notice', 'Solution', 'CS', 'News'];

function getVisiblePageIndexes(currentPage, totalPages) {
  const windowSize = 5;
  if (totalPages <= windowSize) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const half = Math.floor(windowSize / 2);
  let start = Math.max(currentPage - half, 0);
  let end = start + windowSize - 1;

  if (end > totalPages - 1) {
    end = totalPages - 1;
    start = end - windowSize + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

// [기술] HTML date 입력값("2026-05-12")을 표시 형식("2026.05.12")으로 변환합니다.
function formatDateForStorage(htmlDate) {
  return htmlDate.replace(/-/g, '.');
}

// [기술] 저장된 날짜("2026.05.12")를 HTML date 입력 형식("2026-05-12")으로 변환합니다.
function parseStoredDate(storedDate) {
  return storedDate ? storedDate.replace(/\./g, '-') : getTodayHtmlDate();
}

function getTodayHtmlDate() {
  return new Date().toISOString().slice(0, 10);
}

function makeEmptyForm() {
  return { category: 'Notice', date: getTodayHtmlDate(), title: '', summary: '', body: '' };
}

export default function NewsListPage({ onNavigate, adminTrigger }) {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  // 관리자 모드 상태
  const [triggerCount, setTriggerCount] = useState(0);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [adminKeyError, setAdminKeyError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [adminMode, setAdminMode] = useState(false);

  // 뉴스 등록/수정 폼 상태
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = 등록 모드, string = 수정 모드
  const [newItem, setNewItem] = useState(makeEmptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const triggerTimerRef = useRef(null);
  const formRef = useRef(null);

  // [비즈니스] /news/news_edit 경로로 진입한 경우 페이지 로드 즉시 관리자 인증 모달을 엽니다.
  useEffect(() => {
    if (adminTrigger) {
      setShowAdminModal(true);
    }
  }, [adminTrigger]);

  // [기능] 백엔드 API에서 뉴스 목록을 가져옵니다.
  useEffect(() => {
    fetch('/api/news')
      .then((r) => r.json())
      .then((data) => { setNewsItems(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const totalPages = Math.ceil(newsItems.length / NEWS_PAGE_SIZE);
  const pageIndexes = getVisiblePageIndexes(currentPage, totalPages);
  const isFirstPage = currentPage <= 0;
  const isLastPage = totalPages === 0 || currentPage >= totalPages - 1;

  const visibleNewsItems = useMemo(() => {
    const startIndex = currentPage * NEWS_PAGE_SIZE;
    return newsItems.slice(startIndex, startIndex + NEWS_PAGE_SIZE);
  }, [newsItems, currentPage]);

  const goToPage = (pageIndex) => {
    setCurrentPage(Math.min(Math.max(pageIndex, 0), totalPages - 1));
  };

  const goToDetail = (slug) => {
    onNavigate(`/news/${slug}.html`);
  };

  const handleRowKeyDown = (event, slug) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goToDetail(slug);
    }
  };

  // [비즈니스] 총 건수 텍스트를 N회 빠르게 클릭하면 관리자 인증 모달을 엽니다.
  const handleStatusClick = () => {
    setTriggerCount((prev) => {
      const next = prev + 1;
      if (next >= ADMIN_TRIGGER_COUNT) {
        setShowAdminModal(true);
        clearTimeout(triggerTimerRef.current);
        return 0;
      }
      clearTimeout(triggerTimerRef.current);
      triggerTimerRef.current = setTimeout(() => setTriggerCount(0), ADMIN_TRIGGER_RESET_MS);
      return next;
    });
  };

  const handleVerifyAdmin = async () => {
    setIsVerifying(true);
    setAdminKeyError('');
    try {
      const res = await fetch('/api/news/verify', {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKeyInput },
      });
      if (res.ok) {
        setAdminKey(adminKeyInput);
        setAdminMode(true);
        setShowAdminModal(false);
        setAdminKeyInput('');
        // [비즈니스] URL에 남아있는 관리자 진입 경로(/news/news_edit)를 정리합니다.
        if (adminTrigger) {
          onNavigate('/news/index.html');
        }
      } else {
        setAdminKeyError('관리자 키가 올바르지 않습니다.');
      }
    } catch {
      setAdminKeyError('서버 오류가 발생했습니다.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancelAdminModal = () => {
    setShowAdminModal(false);
    setAdminKeyInput('');
    setAdminKeyError('');
  };

  // [기능] 등록(POST) 또는 수정(PUT) 요청을 처리합니다. editingSlug 여부로 모드를 구분합니다.
  const handleSubmitForm = async () => {
    if (!newItem.title.trim() || !newItem.date) return;
    setIsSubmitting(true);
    setFormError('');
    // [기술] 클로저 캡처 시점 문제를 방지하기 위해 로컬 변수로 복사합니다.
    const currentEditingSlug = editingSlug;
    try {
      const payload = {
        category: newItem.category,
        date: formatDateForStorage(newItem.date),
        title: newItem.title.trim(),
        summary: newItem.summary.trim(),
        // [기술] 본문 텍스트의 줄바꿈을 단락 배열로 변환합니다.
        body: newItem.body.split('\n').filter((line) => line.trim() !== ''),
      };
      const url = currentEditingSlug ? `/api/news/${currentEditingSlug}` : '/api/news';
      const method = currentEditingSlug ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const saved = await res.json();
        if (currentEditingSlug) {
          // [비즈니스] 수정 시 목록 내 위치를 유지하면서 해당 항목만 교체합니다.
          setNewsItems((prev) => prev.map((item) => item.slug === currentEditingSlug ? saved : item));
        } else {
          setNewsItems((prev) => [saved, ...prev]);
          setCurrentPage(0);
        }
        setShowAddForm(false);
        setEditingSlug(null);
        setNewItem(makeEmptyForm());
        setFormError('');
      } else {
        // [비즈니스] 서버 오류 응답 시 원인을 폼에 표시해 관리자가 문제를 파악할 수 있게 합니다.
        const errorText = await res.text().catch(() => '');
        setFormError(`저장 실패 (${res.status})${errorText ? ': ' + errorText : ''}`);
      }
    } catch (e) {
      setFormError('네트워크 오류가 발생했습니다. 서버가 실행 중인지 확인해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // [기능] 수정 버튼 클릭 시 해당 항목 데이터를 폼에 채우고 수정 모드로 전환합니다.
  const handleEditNews = (item, e) => {
    e.stopPropagation();
    setEditingSlug(item.slug);
    setFormError('');
    setNewItem({
      category: item.category,
      date: parseStoredDate(item.date),
      title: item.title,
      summary: item.summary ?? '',
      body: (item.body ?? []).join('\n'),
    });
    setShowAddForm(true);
    // [기술] 폼이 테이블 위에 렌더링되므로 스크롤을 올려 폼이 보이게 합니다.
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('이 뉴스를 삭제하시겠습니까?')) return;
    const res = await fetch(`/api/news/${slug}`, {
      method: 'DELETE',
      headers: { 'X-Admin-Key': adminKey },
    });
    if (res.ok) {
      setNewsItems((prev) => prev.filter((item) => item.slug !== slug));
    }
  };

  return (
    <>
      <PageHero title={pageMeta.newsList.title} trail={pageMeta.newsList.trail} className="news-page-hero" />
      <section className="section">
        <div className="container">
          <div className="news-list-panel fade-in">

            <div className="news-status-row">
              <p className="news-page-status" onClick={handleStatusClick}>
                총 {loading ? '…' : newsItems.length}건
              </p>
              {adminMode && (
                <button
                  type="button"
                  className="news-admin-add-btn"
                  onClick={() => {
                if (showAddForm && editingSlug) {
                  // [비즈니스] 수정 모드에서 "닫기" 클릭 시 등록 모드로 초기화합니다.
                  setEditingSlug(null);
                  setNewItem(makeEmptyForm());
                }
                setShowAddForm((v) => !v);
              }}
                >
                  {showAddForm ? '닫기' : '+ 뉴스 추가'}
                </button>
              )}
            </div>

            {adminMode && showAddForm && (
              <div className="news-admin-form" ref={formRef}>
                <p className="news-admin-form-title">{editingSlug ? '뉴스 수정' : '새 뉴스 등록'}</p>

                <div className="news-admin-form-row">
                  <div style={{ flex: '0 0 160px' }}>
                    <label>카테고리</label>
                    <select
                      value={newItem.category}
                      onChange={(e) => setNewItem((v) => ({ ...v, category: e.target.value }))}
                    >
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: '0 0 180px' }}>
                    <label>날짜</label>
                    <input
                      type="date"
                      value={newItem.date}
                      onChange={(e) => setNewItem((v) => ({ ...v, date: e.target.value }))}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>제목 *</label>
                    <input
                      type="text"
                      placeholder="제목을 입력하세요"
                      value={newItem.title}
                      onChange={(e) => setNewItem((v) => ({ ...v, title: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="news-admin-form-row full">
                  <label>요약</label>
                  <input
                    type="text"
                    placeholder="목록에 표시할 요약 문장"
                    value={newItem.summary}
                    onChange={(e) => setNewItem((v) => ({ ...v, summary: e.target.value }))}
                  />
                </div>

                <div className="news-admin-form-row full">
                  <label>본문 (줄바꿈 = 단락 구분)</label>
                  <textarea
                    rows={5}
                    placeholder={'첫 번째 단락\n\n두 번째 단락'}
                    value={newItem.body}
                    onChange={(e) => setNewItem((v) => ({ ...v, body: e.target.value }))}
                  />
                </div>

                {formError && (
                  <p style={{ color: '#c0392b', fontSize: '0.85rem', marginTop: '8px', marginBottom: '0' }}>
                    {formError}
                  </p>
                )}

                <div className="news-admin-form-actions">
                  <button
                    type="button"
                    className="news-admin-cancel-btn"
                    onClick={() => { setShowAddForm(false); setEditingSlug(null); setNewItem(makeEmptyForm()); setFormError(''); }}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="news-admin-submit-btn"
                    onClick={handleSubmitForm}
                    disabled={isSubmitting || !newItem.title.trim()}
                  >
                    {isSubmitting ? '처리 중...' : editingSlug ? '수정' : '등록'}
                  </button>
                </div>
              </div>
            )}

            <div className="news-list-table-wrap">
              <table className="news-list-table">
                <tbody>
                  {visibleNewsItems.map((item, index) => {
                    const itemNumber = newsItems.length - (currentPage * NEWS_PAGE_SIZE + index);

                    return (
                      <tr
                        key={`${item.slug}-${currentPage}-${index}`}
                        className="news-list-row"
                        role="button"
                        tabIndex={0}
                        onClick={() => goToDetail(item.slug)}
                        onKeyDown={(event) => handleRowKeyDown(event, item.slug)}
                      >
                        <td>{itemNumber}</td>
                        <td>
                          <span className="news-category-badge">{item.category}</span>
                        </td>
                        <td className="news-list-title">
                          <span>{item.title}</span>
                        </td>
                        <td>{item.date}</td>
                        {adminMode && (
                          <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                            <button
                              type="button"
                              className="news-admin-edit-btn"
                              onClick={(e) => handleEditNews(item, e)}
                            >
                              수정
                            </button>
                            <button
                              type="button"
                              className="news-admin-delete-btn"
                              onClick={() => handleDelete(item.slug)}
                            >
                              삭제
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <nav className="news-pagination" aria-label="뉴스 목록 페이지 이동">
                <div className="news-page-controls">
                  <button
                    type="button"
                    className="page-nav-btn page-nav-edge"
                    onClick={() => goToPage(0)}
                    disabled={isFirstPage}
                    aria-label="첫 페이지"
                  >
                    <ChevronsLeft size={15} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="page-nav-btn page-nav-text"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={isFirstPage}
                    aria-label="이전 페이지"
                  >
                    <ChevronLeft size={14} strokeWidth={2} />
                  </button>
                  {pageIndexes.map((pageIndex) => (
                    <button
                      key={pageIndex}
                      type="button"
                      className={`page-number-button ${pageIndex === currentPage ? 'is-active' : ''}`}
                      onClick={() => goToPage(pageIndex)}
                      aria-current={pageIndex === currentPage ? 'page' : undefined}
                    >
                      {pageIndex + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="page-nav-btn page-nav-text"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={isLastPage}
                    aria-label="다음 페이지"
                  >
                    <ChevronRight size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="page-nav-btn page-nav-edge"
                    onClick={() => goToPage(totalPages - 1)}
                    disabled={isLastPage}
                    aria-label="마지막 페이지"
                  >
                    <ChevronsRight size={15} strokeWidth={2} />
                  </button>
                </div>
              </nav>
            ) : null}
          </div>
        </div>
      </section>

      <PasswordVerifyModal
        isOpen={showAdminModal}
        password={adminKeyInput}
        errorMessage={adminKeyError}
        isSubmitting={isVerifying}
        onChangePassword={(e) => setAdminKeyInput(e.target.value)}
        onConfirm={handleVerifyAdmin}
        onCancel={handleCancelAdminModal}
      />
    </>
  );
}
